import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../lib/apiClient";
import { sgtToday } from "../lib/datetime";
import type {
  ExclusionsResponse,
  LinkableEmployeesResponse,
  SyncExclusion,
} from "../lib/types";

/**
 * An entry applies through the whole of its expiry day (mirrors `isExpired` in
 * api/src/services/exclusions.ts — both compare SGT calendar dates).
 */
export function isExpired(row: SyncExclusion, today = sgtToday()): boolean {
  return row.expiresAt !== null && row.expiresAt < today;
}

/** Mirror of the API's canonicalEmail (SPEC §3.2) — lowercase, strip `+tag`. */
export function canonicalEmail(raw: string): string {
  const e = raw.trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at <= 0) return e;
  const local = e.slice(0, at);
  const domain = e.slice(at + 1);
  return `${local.split("+", 1)[0] ?? local}@${domain}`;
}

export type ExclusionSource = "manual" | "env" | "admin";

export interface ExclusionsApi {
  data: ExclusionsResponse | null;
  error: string | null;
  busy: boolean;
  reload: () => Promise<void>;
  /** Which list a canonical email is on, or null if it isn't exempt. */
  sourceOf: (email: string | null | undefined) => ExclusionSource | null;
  add: (
    email: string,
    opts?: {
      note?: string;
      employeeNo?: string | null;
      /** SGT `YYYY-MM-DD`; `null` clears any end date. */
      expiresAt?: string | null;
    },
  ) => Promise<void>;
  remove: (canonical: string) => Promise<void>;
  /** Load the linkable-employee pool on demand (the source is slow). */
  loadLinkable: () => Promise<LinkableEmployeesResponse>;
}

/**
 * Loads the effective never-lock allowlist and exposes add/remove. Shared by the
 * Settings screen and the per-row buttons on a run's action list.
 */
export function useExclusions(enabled = true): ExclusionsApi {
  const { request } = useApi();
  const [data, setData] = useState<ExclusionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      setData(await request<ExclusionsResponse>("/api/admin/exclusions"));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [enabled, request]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const bySource = useMemo(() => {
    const today = sgtToday();
    const m = new Map<string, ExclusionSource>();
    for (const e of data?.envExclusions ?? []) m.set(e, "env");
    for (const e of data?.adminExclusions ?? []) m.set(e, "admin");
    // Manual rows win the label — they're the only editable source. An expired
    // row is deliberately NOT counted: it no longer exempts anyone, and showing
    // it as protection would be a lie the engine doesn't tell.
    for (const e of data?.exclusions ?? []) {
      if (isExpired(e, today)) m.delete(e.canonicalEmail);
      else m.set(e.canonicalEmail, "manual");
    }
    return m;
  }, [data]);

  const sourceOf = useCallback(
    (email: string | null | undefined) =>
      email ? (bySource.get(canonicalEmail(email)) ?? null) : null,
    [bySource],
  );

  const mutate = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const add = useCallback(
    (
      email: string,
      opts: {
        note?: string;
        employeeNo?: string | null;
        expiresAt?: string | null;
      } = {},
    ) =>
      mutate(() =>
        request("/api/admin/exclusions", {
          method: "POST",
          body: JSON.stringify({
            email,
            // Omit rather than send undefined — absent means "leave unchanged".
            ...(opts.note?.trim() ? { note: opts.note } : {}),
            ...(opts.employeeNo === undefined
              ? {}
              : { employeeNo: opts.employeeNo }),
            ...(opts.expiresAt === undefined
              ? {}
              : { expiresAt: opts.expiresAt || null }),
          }),
        }),
      ),
    [mutate, request],
  );

  const loadLinkable = useCallback(
    () => request<LinkableEmployeesResponse>("/api/admin/employees/linkable"),
    [request],
  );

  const remove = useCallback(
    (canonical: string) =>
      mutate(() =>
        request(`/api/admin/exclusions/${encodeURIComponent(canonical)}`, {
          method: "DELETE",
        }),
      ),
    [mutate, request],
  );

  return { data, error, busy, reload, sourceOf, add, remove, loadLinkable };
}

const SOURCE_LABEL: Record<ExclusionSource, string> = {
  manual: "On allowlist",
  env: "Allowlisted (env)",
  admin: "Exempt (app admin)",
};

/**
 * Per-row control on a run's action list: allowlist this email so future runs
 * skip it. Already-exempt emails render as a static label instead of a button.
 */
export function ExcludeButton({
  email,
  ex,
  onAdded,
}: {
  email: string | null;
  ex: ExclusionsApi;
  onAdded?: () => void;
}) {
  const [justAdded, setJustAdded] = useState(false);
  const source = ex.sourceOf(email);

  if (!email) return <span className="muted">—</span>;
  if (source) return <span className="muted">{SOURCE_LABEL[source]}</span>;

  return (
    <button
      className="btn-inline"
      disabled={ex.busy || justAdded}
      title={`Never lock ${canonicalEmail(email)} in future syncs`}
      onClick={() => {
        void ex
          .add(email, { note: "added from run actions" })
          .then(() => {
            setJustAdded(true);
            onAdded?.();
          })
          .catch(() => {
            /* error surfaced via ex.error */
          });
      }}
    >
      {justAdded ? "Added" : "Never lock"}
    </button>
  );
}
